import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Select, Tooltip, Typography } from "antd";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE, LOG_DATE_FORMAT } from "@/config";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";
import { dateFormat } from "@/utils";
import type { MessageFeedback, MessageFeedbackPaingParams } from "@zhihaoscm/entity";
import { MESSAGE_FEEDBACK_ORIGIN, MESSAGE_FEEDBACK_STATE, MESSAGE_FEEDBACK_TYPE, SAND_ORGIM } from "@zhihaoscm/meta";

export interface MessageFeedbackListResult {
    list: MessageFeedback[];
    total: number;
}

export interface MessageFeedbackPageQuery extends Omit<MessageFeedbackPaingParams, "origin"> {
    page: number;
    pageSize: number;
    origins?: string[];
}

export interface MessageFeedbackPageProps {
    loadFeedback?: (query: MessageFeedbackPageQuery) => Promise<MessageFeedbackListResult>;
    replyFeedback?: (id: number, payload: { replyContent: string }) => Promise<unknown>;
    permissions?: string[];
}

const DEFAULT_QUERY: MessageFeedbackPageQuery = { page: 1, pageSize: 10 };
const DEFAULT_DRAFT = {
    keywords: "",
    type: "",
    origins: [] as string[],
    state: "",
    respondentName: "",
    beginDate: "",
    endDate: ""
};
const typeOptions = MESSAGE_FEEDBACK_TYPE.options.map(item => ({ value: item.value, label: item.label }));
const originOptions = SAND_ORGIM.options.map(item => ({ value: item.value, label: item.label }));
const stateOptions = MESSAGE_FEEDBACK_STATE.options.map(item => ({ value: item.value, label: item.label }));

/** 沿用 Vue MessageFeedbackService.paging 的留言反馈分页接口。 */
export async function loadMessageFeedback(query: MessageFeedbackPageQuery): Promise<MessageFeedbackListResult> {
    const response = await request.service.get<MessageFeedbackListResult>("/admin/message-feedback/paging", {
        params: query
    });
    return response.data;
}

/** 沿用 Vue 回复留言反馈接口及 replyContent payload。 */
export async function replyMessageFeedback(id: number, payload: { replyContent: string }): Promise<unknown> {
    const response = await request.service.put(`/admin/message-feedback/reply/${id}`, payload);
    return response.data;
}

/** 将 Vue 客户 JSON 信息映射为留言反馈账号文案。 */
function getCustomerLabel(row: MessageFeedback): string {
    const customer = row.commenterEnterprise;
    return (
        [customer?.institutionName, customer?.mobile, customer?.code, customer?.appTypeName]
            .filter(Boolean)
            .join(" | ") || "--"
    );
}

/** 对齐 Vue 列配置的 LOG_DATE_FORMAT，空时间回退到统一默认占位。 */
function formatFeedbackTime(value?: string): string {
    return value ? dateFormat(value, LOG_DATE_FORMAT) : DEFAULT_VALUE;
}

/** 用户留言反馈列表，保留 Vue 字段、状态权限与回复入口。 */
export function MessageFeedbackPage({
    loadFeedback = loadMessageFeedback,
    replyFeedback = replyMessageFeedback,
    permissions
}: MessageFeedbackPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 沿用 Vue 全局封禁态，留言回复入口保留但禁止触发。
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const [query, setQuery] = useState<MessageFeedbackPageQuery>(DEFAULT_QUERY);
    const [draft, setDraft] = useState(DEFAULT_DRAFT);
    const [items, setItems] = useState<MessageFeedback[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [replyContent, setReplyContent] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const canReply = currentPermissions.includes("ROLE_SS_FEEDBACK_W");

    /** 将 Vue 留言反馈筛选条件转换为分页请求参数。 */
    const submitFilters = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const beginDate = draft.beginDate.trim();
        const endDate = draft.endDate.trim();
        if (Boolean(beginDate) !== Boolean(endDate)) {
            setErrorMessage("请选择完整留言日期范围");
            return;
        }
        if (beginDate && endDate && beginDate > endDate) {
            setErrorMessage("留言开始日期不能晚于结束日期");
            return;
        }
        const nextQuery: MessageFeedbackPageQuery = { ...DEFAULT_QUERY };
        if (draft.keywords.trim()) nextQuery.keywords = draft.keywords.trim();
        if (draft.type) nextQuery.type = Number(draft.type);
        if (draft.origins.length > 0) nextQuery.origins = draft.origins;
        if (draft.state) nextQuery.state = Number(draft.state);
        if (draft.respondentName.trim()) nextQuery.respondentName = draft.respondentName.trim();
        if (beginDate && endDate) {
            nextQuery.beginTime = `${beginDate}T00:00:00`;
            nextQuery.endTime = `${endDate}T23:59:59`;
        }
        setErrorMessage(null);
        setQuery(nextQuery);
    };

    /** 对齐 Vue ProTable 的重置动作，清空草稿筛选并回到第一页。 */
    const resetFilters = () => {
        setDraft({ ...DEFAULT_DRAFT, origins: [] });
        setErrorMessage(null);
        setQuery({ ...DEFAULT_QUERY });
    };

    /** 提交 Vue 回复 payload，成功后刷新当前页。 */
    const submitReply = async () => {
        if (selectedId == null) return;
        if (!replyContent.trim()) {
            setErrorMessage("请输入回复内容");
            return;
        }
        setSubmitting(true);
        try {
            await replyFeedback(selectedId, { replyContent: replyContent.trim() });
            setSelectedId(null);
            setReplyContent("");
            const result = await loadFeedback(query);
            setItems(result.list || []);
            setTotal(Number(result.total) || 0);
            setErrorMessage(null);
        } catch {
            setErrorMessage("留言回复失败");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        let disposed = false;
        void loadFeedback(query)
            .then(result => {
                if (!disposed) {
                    setItems(result.list || []);
                    setTotal(Number(result.total) || 0);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setItems([]);
                    setTotal(0);
                    setErrorMessage("用户留言反馈列表加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadFeedback, query]);

    /** 将 zhihao-ui-react 排序字段转换为 Vue 留言反馈分页接口参数。 */
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        const sortKey = prop === "replyTime" ? "reply_time" : "created_time";
        setQuery(current => {
            const nextQuery: MessageFeedbackPageQuery = { ...current, page: 1 };
            if (order) {
                nextQuery.sortKey = sortKey;
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            } else {
                delete nextQuery.sortKey;
                delete nextQuery.sortOrder;
            }
            return nextQuery;
        });
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 留言反馈表格的字段、排序和操作插槽。
    const columns: TableColumnProps<MessageFeedback>[] = [
        {
            label: "编号",
            width: 106,
            key: "id",
            prop: "id",
            render: ({ row }) => <Link to={`/userOperation/messageFeedback/details?id=${row.id}`}>{row.id}</Link>
        },
        {
            label: "反馈账号",
            width: 285,
            key: "customer",
            prop: "commenterEnterprise",
            render: ({ row }) => getCustomerLabel(row)
        },
        {
            label: "内容",
            key: "content",
            prop: "content",
            render: ({ row }) => {
                const displayContent = row.content.length > 30 ? `${row.content.slice(0, 30)}...` : row.content;
                // 对齐 Vue el-tooltip：少于 30 字不弹浮层，超长内容查看时仍保留完整原文。
                return (
                    <Tooltip placement="top" title={row.content.length < 30 ? undefined : row.content}>
                        <span>{displayContent}</span>
                    </Tooltip>
                );
            }
        },
        {
            label: "类型",
            width: 134,
            key: "type",
            prop: "type",
            render: ({ row }) => MESSAGE_FEEDBACK_TYPE.getIntroByCode(Number(row.type)) || "-"
        },
        {
            label: "信息来源",
            width: 134,
            key: "origin",
            prop: "origin",
            render: ({ row }) => MESSAGE_FEEDBACK_ORIGIN.getIntroByCode(Number(row.origin)) || "-"
        },
        {
            label: "回复人",
            width: 120,
            key: "respondentName",
            prop: "respondentName",
            render: ({ row }) => row.respondentName || DEFAULT_VALUE
        },
        {
            label: "留言时间",
            width: 204,
            prop: "createdTime",
            key: "createdTime",
            render: ({ row }) => formatFeedbackTime(row.createdTime),
            sortable: true
        },
        {
            label: "回复时间",
            width: 204,
            prop: "replyTime",
            key: "replyTime",
            render: ({ row }) => formatFeedbackTime(row.replyTime),
            sortable: true
        },
        {
            label: "状态",
            fixed: "right",
            width: 120,
            key: "state",
            prop: "state",
            render: ({ row }) => MESSAGE_FEEDBACK_STATE.getIntroByCode(Number(row.state)) || "-"
        },
        {
            label: "操作",
            fixed: "right",
            width: 106,
            key: "operation",
            prop: "id",
            className: "admin-table-operation-column compact",
            labelClassName: "admin-table-operation-column compact",
            render: ({ row }) =>
                Number(row.state) === 0 && canReply ? (
                    <Button
                        aria-label="回复"
                        disabled={banButton}
                        onClick={() => {
                            setSelectedId(Number(row.id));
                            setReplyContent("");
                            setErrorMessage(null);
                        }}
                    >
                        回复
                    </Button>
                ) : null
        }
    ];

    return (
        <section aria-label="message-feedback-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<MessageFeedback>
                    data={items}
                    columns={normalizeOperationColumns(columns, items)}
                    pagination={false}
                    rowKey="id"
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                用户留言反馈
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <form className="admin-table-filter-form mb-4" onSubmit={submitFilters}>
                                <Input
                                    className="message-feedback-keyword-filter"
                                    aria-label="反馈账号或编号或内容关键词"
                                    placeholder="请输入反馈账号或编号或内容关键词"
                                    value={draft.keywords}
                                    onChange={event =>
                                        setDraft(current => ({ ...current, keywords: event.target.value }))
                                    }
                                />
                                <Select
                                    className="message-feedback-type-filter"
                                    aria-label="留言类型"
                                    options={typeOptions}
                                    placeholder="请选择类型"
                                    value={draft.type || undefined}
                                    onChange={value => setDraft(current => ({ ...current, type: value }))}
                                />
                                <Select
                                    className="message-feedback-origin-filter"
                                    aria-label="信息来源"
                                    mode="multiple"
                                    options={originOptions}
                                    placeholder="请选择来源"
                                    value={draft.origins}
                                    onChange={value =>
                                        setDraft(current => ({
                                            ...current,
                                            origins: value.map((item: string | number) => String(item))
                                        }))
                                    }
                                />
                                <Input
                                    className="message-feedback-respondent-filter"
                                    aria-label="回复人"
                                    placeholder="请输入回复人"
                                    value={draft.respondentName}
                                    onChange={event =>
                                        setDraft(current => ({ ...current, respondentName: event.target.value }))
                                    }
                                />
                                <Select
                                    className="message-feedback-state-filter"
                                    aria-label="回复状态"
                                    options={stateOptions}
                                    placeholder="请选择类型"
                                    value={draft.state || undefined}
                                    onChange={value => setDraft(current => ({ ...current, state: value }))}
                                />
                                <ZhDateRangePicker
                                    type="daterange"
                                    format="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="留言开始日期"
                                    endPlaceholder="留言结束日期"
                                    value={[draft.beginDate, draft.endDate]}
                                    onChange={value =>
                                        setDraft(current => ({
                                            ...current,
                                            beginDate: String(value?.[0] || ""),
                                            endDate: String(value?.[1] || "")
                                        }))
                                    }
                                />
                                <Button
                                    aria-label="查询留言反馈"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置留言反馈"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={resetFilters}
                                />
                            </form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4 self-end"
                                current={query.page}
                                pageSize={query.pageSize}
                                showSizeChanger
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.pageSize ? 1 : page,
                                        pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                alignCenter
                modalClass="user-operation-reply-dialog"
                open={selectedId !== null}
                title="回复"
                width={493}
                closeOnClickModal={false}
                closeOnPressEscape={false}
                onCancel={() => setSelectedId(null)}
                footer={
                    <>
                        <Button aria-label="取消" onClick={() => setSelectedId(null)}>
                            取消
                        </Button>
                        <Button
                            aria-label="确认回复"
                            loading={submitting}
                            onClick={() => void submitReply()}
                            type="primary"
                        >
                            确认
                        </Button>
                    </>
                }
            >
                <div className="user-operation-action-dialog-form">
                    <label className="user-operation-action-dialog-form-item user-operation-action-dialog-form-item--reply">
                        <span className="user-operation-action-dialog-form-label">
                            <span className="user-operation-action-dialog-required">*</span>
                            回复内容
                        </span>
                        <span className="user-operation-action-dialog-form-control">
                            <Input.TextArea
                                aria-label="回复内容"
                                className="user-operation-action-dialog-textarea"
                                maxLength={200}
                                placeholder="请输入内容"
                                rows={2}
                                showCount
                                value={replyContent}
                                onChange={event => setReplyContent(event.target.value)}
                            />
                        </span>
                    </label>
                </div>
            </ZhDialog>
        </section>
    );
}

export default MessageFeedbackPage;
